import {
    CarnivalTreasureBetType,
    CarnivalTreasureOwnResult,
    CarnivalTreasureRecord,
    CarnivalTreasureResult,
} from '@/models/games/carnivalTreasure';
import { CarnivalTreasureColor } from '@/models/games/carnivalTreasure/Record';
import { LocalState } from '@/models/games/enums/LocalState';
import { GameState } from '@/models/games/enums/GameState';
import { Multiplier } from '@/models/games/carnivalTreasure/Multiplier';
import { SupportedGameResult } from '@/modules/host/models';

const resultMapping: { [key: number]: number } = {
    0: 1,
    1: 2,
    2: 4,
    3: 7,
    4: 18,
    5: 40,
};

const colorEnumMap: Record<string, CarnivalTreasureColor> = {
    '1': CarnivalTreasureColor.BLUE,
    '2': CarnivalTreasureColor.GREEN,
    '4': CarnivalTreasureColor.YELLOW,
    '7': CarnivalTreasureColor.PURPLE,
    '18': CarnivalTreasureColor.ORANGE,
    '40': CarnivalTreasureColor.RED,
    '2X': CarnivalTreasureColor.BONUS_2X,
    '10X': CarnivalTreasureColor.BONUS_10X,
};

const colorMap: Record<CarnivalTreasureColor, string> = {
    [CarnivalTreasureColor.BLUE]: '#355D9D',
    [CarnivalTreasureColor.GREEN]: '#109E74',
    [CarnivalTreasureColor.YELLOW]: '#DDBA5D',
    [CarnivalTreasureColor.PURPLE]: '#8E31C5',
    [CarnivalTreasureColor.ORANGE]: '#DF743C',
    [CarnivalTreasureColor.RED]: '#DF3737',
    [CarnivalTreasureColor.BONUS_2X]: '#A1355C',
    [CarnivalTreasureColor.BONUS_10X]: '#AF7C4B',
};

export const getResultSet = (): Record<number, string[]> => {
    return {
        0: ['1', '40', '1'],
        1: ['40', '1', '2'],
        2: ['1', '2', '1'],
        3: ['2', '1', '4'],
        4: ['1', '4', '1'],
        5: ['4', '1', '7'],
        6: ['1', '7', '2'],
        7: ['7', '2', '1'],
        8: ['2', '1', '18'],
        9: ['1', '18', '1'],
        10: ['18', '1', '2'],

        11: ['1', '2', '4'],
        12: ['2', '4', '2'],
        13: ['4', '2', '1'],
        14: ['2', '1', '10X'],
        16: ['10X', '1', '4'],
        17: ['1', '4', '1'],
        18: ['4', '1', '2'],
        19: ['1', '2', '1'],
        20: ['2', '1', '7'],

        21: ['1', '7', '2'],
        22: ['7', '2', '1'],
        23: ['2', '1', '4'],
        24: ['1', '4', '2'],
        25: ['4', '2', '1'],
        26: ['2', '1', '7'],
        27: ['1', '7', '1'],
        28: ['7', '1', '2'],
        29: ['1', '2', '4'],
        30: ['2', '4', '1'],

        31: ['4', '1', '2'],
        32: ['1', '2', '1'],
        33: ['2', '1', '7'],
        34: ['1', '7', '1'],
        35: ['7', '1', '18'],
        36: ['1', '18', '1'],
        37: ['18', '1', '2'],
        38: ['1', '2', '4'],
        39: ['2', '4', '2'],
        40: ['4', '2', '1'],

        41: ['2', '1', '2X'],
        43: ['2X', '1', '2'],
        44: ['1', '2', '4'],
        45: ['2', '4', '1'],
        46: ['4', '1', '7'],
        47: ['1', '7', '1'],
        48: ['7', '1', '2'],
        49: ['1', '2', '1'],
        50: ['2', '1', '4'],

        51: ['1', '4', '2'],
        52: ['4', '2', '1'],
        53: ['2', '1', '40'],
    };
};

export const getResultByResultIndex = (index: number): number => {
    return resultMapping[index];
};

export const getColorByResult = (result: string): string => {
    return colorMap[colorEnumMap[result]];
};

export const getMultiplierByMultiplierIndex = (index: Multiplier): number => {
    switch (index) {
        case Multiplier.TCTMTwoX:
            return 2;
        case Multiplier.TCTMTenX:
            return 10;
    }
};

export const getMaxFinal = (mf: number): number => {
    return Math.min(mf, 10000);
};

export const convertDetailResultString = (
    json: string
): CarnivalTreasureOwnResult => {
    const data = JSON.parse(json);

    return {
        MultiplierSize: data.MS,
        Odds: data.O,
        Multipliters: data.M,
        MultipliterFinal: data.MF,
        PayoutFinal: data.PF,
        ResultPoint: resultMapping[data.R],
        ResultIndex: data.I,
    };
};

export const resultToColor = (result: CarnivalTreasureBetType) => {
    switch (result) {
        case CarnivalTreasureBetType.TCTBOne:
            return CarnivalTreasureColor.BLUE;
        case CarnivalTreasureBetType.TCTBTwo:
            return CarnivalTreasureColor.GREEN;
        case CarnivalTreasureBetType.TCTBFour:
            return CarnivalTreasureColor.YELLOW;
        case CarnivalTreasureBetType.TCTBSeven:
            return CarnivalTreasureColor.PURPLE;
        case CarnivalTreasureBetType.TCTBEighteen:
            return CarnivalTreasureColor.ORANGE;
        case CarnivalTreasureBetType.TCTBForty:
            return CarnivalTreasureColor.RED;
    }
};

export const convertResultToRecord = (
    result: CarnivalTreasureResult
): CarnivalTreasureRecord => {
    const record: CarnivalTreasureRecord = {
        gameID: result.GameID,
        result: result.Result,
        resultIndex: result.ResultIndex,
        resultPoint: result.ResultPoint,
        round: result.GameCount % 10000,
        shoes: Math.floor(result.GameCount),
        color: resultToColor(result.ResultPoint),
        payout: Number(result.PayoutFinal),
        multiples: result.MultiplierSize > 0,
    };

    return record;
};

export const convertResultStringToRecord = (
    currentResult: SupportedGameResult,
    resultString: string,
    result?: number
): CarnivalTreasureRecord => {
    const { FResult, GameID, Result, GameCount } = currentResult;
    const updateResult: CarnivalTreasureResult = {
        FResult: FResult,
        GameCount: GameCount,
        GameID: GameID,
        Result: result ?? Result,
        ...convertDetailResultString(resultString),
    };

    const resultRecord = convertResultToRecord(updateResult);
    return resultRecord;
};

export const getLocalState = (
    isRest: boolean,
    state: number,
    isGameResultReleased: boolean
) => {
    if (isRest) {
        return LocalState.REST;
    }

    switch (state) {
        // case GameState.RoundCancel:
        //     return LocalState.ROUND_CANCEL;
        case GameState.Restart:
            return GameState.Restart;
        case GameState.Idle:
        case GameState.Shuffle:
            return LocalState.IDLE;
        case GameState.Betting:
            return LocalState.BETTING;
        case GameState.Started:
            return LocalState.DEALING;
        default:
            if (state > GameState.Started && state < GameState.GameResult) {
                //dirty logic to prevent enum conflict between game state and local state
                return LocalState.FREE_SPIN + state;
            }
            break;
    }

    if (isGameResultReleased) {
        return LocalState.RESULT;
    }

    return LocalState.IDLE;
};
